import React, { useMemo, useState } from "react";
import {
  Button,
  Collapse,
  Empty,
  Form,
  Input,
  Modal,
  Select,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CodeOutlined,
  MinusOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import {
  normalizeVariables,
  serializeVariables,
  VARIABLE_TYPE_OPTIONS,
  type TypedVariable,
  type VariableType,
} from "./helpers/variables.helper";

const { Text } = Typography;

interface MergeVariable {
  key: string;
  label: string;
  placeholder: string;
  type: VariableType;
  description?: string;
  required?: boolean;
}

interface LockedBindingVar {
  key: string;
  label: string;
  type: string;
}

interface LockedBinding {
  template_code: string;
  label: string;
  variables: LockedBindingVar[];
}

interface Props {
  availableVariables?: any;
  onInsert?: (placeholder: string) => void;
  onAdd?: (variableKey: string) => void;
  onChangeVariables?: (next: any) => void;
  onRemove?: (variableKey: string) => void;
  editable?: boolean;
  lockedBinding?: LockedBinding | null;
}

const CATEGORY_COLORS: Record<string, string> = {
  Candidate: "#3b82f6",
  Job: "#10b981",
  Company: "#8b5cf6",
  Interview: "#f59e0b",
  Offer: "#ef4444",
  Documents: "#f97316",
  Other: "#6b7280",
};

const categorize = (key: string): string => {
  if (key.startsWith("candidate") || key === "applicationCode") return "Candidate";
  if (
    key.startsWith("job") ||
    key === "department" ||
    key === "work_location" ||
    key === "employment_type"
  )
    return "Job";
  if (
    key.startsWith("company") ||
    key === "logoUrl" ||
    key === "supportEmail" ||
    key === "year"
  )
    return "Company";
  if (key.startsWith("interview") || key === "interviewMode") return "Interview";
  if (
    key.startsWith("offer") ||
    key === "joiningDate" ||
    key === "offerDate" ||
    key === "offerLink"
  )
    return "Offer";
  if (
    key === "documentList" ||
    key === "deadlineDate" ||
    key === "uploadLink"
  )
    return "Documents";
  return "Other";
};

const humanize = (key: string): string =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildVariable = (
  v: { key: string; label?: string; type?: VariableType; description?: string; required?: boolean },
): MergeVariable => ({
  key: v.key,
  label: v.label || humanize(v.key),
  placeholder: `{{${v.key}}}`,
  type: v.type || "string",
  description: v.description,
  required: v.required,
});

const groupVariables = (
  variables: MergeVariable[],
): { category: string; color: string; variables: MergeVariable[] }[] => {
  const groups: Record<string, MergeVariable[]> = {};
  for (const v of variables) {
    const cat = categorize(v.key);
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(v);
  }
  return Object.entries(groups).map(([category, vars]) => ({
    category,
    color: CATEGORY_COLORS[category] || "#6b7280",
    variables: vars,
  }));
};

const VARIABLE_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

const MergeVariablesSidebar: React.FC<Props> = ({
  availableVariables,
  onInsert,
  onAdd,
  onChangeVariables,
  onRemove,
  editable = false,
  lockedBinding = null,
}) => {
  const typedVariables = useMemo<TypedVariable[]>(
    () => normalizeVariables(availableVariables),
    [availableVariables],
  );

  const variables = useMemo<MergeVariable[]>(() => {
    if (lockedBinding) {
      return lockedBinding.variables.map((v) =>
        buildVariable({
          key: v.key,
          label: v.label,
          type: (v.type as VariableType) || "string",
        }),
      );
    }
    return typedVariables.map(buildVariable);
  }, [typedVariables, lockedBinding]);

  const isLocked = Boolean(lockedBinding);

  const categories = useMemo(() => groupVariables(variables), [variables]);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newType, setNewType] = useState<VariableType>("string");
  const [newDescription, setNewDescription] = useState("");
  const [newRequired, setNewRequired] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  const openAddModal = () => {
    setNewKey("");
    setNewLabel("");
    setNewType("string");
    setNewDescription("");
    setNewRequired(false);
    setKeyError(null);
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setNewKey("");
    setNewLabel("");
    setNewType("string");
    setNewDescription("");
    setNewRequired(false);
    setKeyError(null);
  };

  const commitVariables = (next: TypedVariable[]) => {
    onChangeVariables?.(serializeVariables(next));
  };

  const handleAddSubmit = () => {
    const trimmed = newKey.trim();
    if (!trimmed) {
      setKeyError("Variable key is required.");
      return;
    }
    if (!VARIABLE_KEY_PATTERN.test(trimmed)) {
      setKeyError(
        "Use letters, numbers, and underscores only. Must start with a letter.",
      );
      return;
    }
    if (typedVariables.some((v) => v.key === trimmed)) {
      setKeyError("This variable is already declared.");
      return;
    }
    const nextVar: TypedVariable = {
      key: trimmed,
      label: newLabel.trim() || undefined,
      type: newType,
      description: newDescription.trim() || undefined,
      required: newRequired || undefined,
    };
    if (onChangeVariables) {
      commitVariables([...typedVariables, nextVar]);
    } else {
      onAdd?.(trimmed);
    }
    closeAddModal();
  };

  const handleRemove = (key: string) => {
    if (onChangeVariables) {
      commitVariables(typedVariables.filter((v) => v.key !== key));
    } else {
      onRemove?.(key);
    }
  };

  const items = categories.map((cat) => ({
    key: cat.category,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: cat.color,
            flexShrink: 0,
          }}
        />
        {cat.category}
      </span>
    ),
    children: (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {cat.variables.map((v) => (
          <Tooltip
            key={v.key}
            title={
              <div style={{ fontSize: 11 }}>
                <div><strong>{v.placeholder}</strong></div>
                <div>Type: {v.type}{v.required ? " · required" : ""}</div>
                {v.description ? (
                  <div style={{ marginTop: 2 }}>{v.description}</div>
                ) : null}
              </div>
            }
          >
            <span
              className="merge-var-chip"
              style={{
                background: `${cat.color}0D`,
                color: cat.color,
                borderColor: `${cat.color}30`,
                cursor: onInsert ? "pointer" : "default",
              }}
              onClick={() => onInsert?.(v.placeholder)}
            >
              <CodeOutlined style={{ fontSize: 10 }} />
              {v.label}
              {v.required ? (
                <span style={{ color: "#dc2626", fontWeight: 600, marginLeft: 2 }}>
                  *
                </span>
              ) : null}
              {v.type && v.type !== "string" ? (
                <span
                  style={{
                    marginLeft: 4,
                    fontSize: 9,
                    padding: "0 4px",
                    borderRadius: 6,
                    background: cat.color,
                    color: "#fff",
                  }}
                >
                  {v.type}
                </span>
              ) : null}
              {editable && !isLocked ? (
                <MinusOutlined
                  style={{ marginLeft: 4, fontSize: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(v.key);
                  }}
                />
              ) : null}
            </span>
          </Tooltip>
        ))}
      </div>
    ),
  }));

  return (
    <div className="merge-sidebar">
      <div className="merge-sidebar-header">
        <h4>
          <CodeOutlined style={{ marginRight: 6, color: "#3b82f6" }} />
          Merge Variables
        </h4>
        <p>
          {isLocked
            ? `Reserved system template — these variables are bound by the backend and cannot be changed.`
            : editable
            ? "Declare each placeholder used in the template. Variables are saved with the template."
            : "Click a variable to insert it at the cursor position."}
        </p>
        {editable && onAdd && !isLocked ? (
          <Button
            type="primary"
            ghost
            size="small"
            icon={<PlusOutlined />}
            onClick={openAddModal}
            style={{ marginTop: 6 }}
          >
            Add variable
          </Button>
        ) : null}
      </div>
      <div className="merge-sidebar-body">
        {variables.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {editable
                  ? "No variables declared yet"
                  : "No variables available for this template"}
              </Text>
            }
          />
        ) : (
          <Collapse
            defaultActiveKey={categories.map((c) => c.category)}
            ghost
            size="small"
            items={items}
          />
        )}
      </div>

      <Modal
        title="Add Merge Variable"
        open={addModalOpen}
        onCancel={closeAddModal}
        onOk={handleAddSubmit}
        okText="Add"
        zIndex={1200}
        destroyOnClose
        width={560}
      >
        <Form layout="vertical">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item
              label="Variable Key"
              required
              help={
                keyError ||
                "Letters, numbers, underscores. e.g. employee_name."
              }
              validateStatus={keyError ? "error" : undefined}
              style={{ marginBottom: 8 }}
            >
              <Input
                autoFocus
                placeholder="employee_name"
                value={newKey}
                onChange={(e) => {
                  setNewKey(e.target.value);
                  if (keyError) setKeyError(null);
                }}
              />
            </Form.Item>

            <Form.Item
              label="Display Label"
              style={{ marginBottom: 8 }}
            >
              <Input
                placeholder="Employee Name"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
            </Form.Item>

            <Form.Item label="Type" style={{ marginBottom: 8 }}>
              <Select
                value={newType}
                onChange={(v) => setNewType(v as VariableType)}
                options={VARIABLE_TYPE_OPTIONS}
              />
            </Form.Item>

            <Form.Item label="Required?" style={{ marginBottom: 8 }}>
              <Switch
                checked={newRequired}
                onChange={setNewRequired}
              />
            </Form.Item>
          </div>

          <Form.Item label="Description" style={{ marginBottom: 8 }}>
            <Input.TextArea
              rows={2}
              placeholder="Optional — shows up as a tooltip and helper text."
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
            />
          </Form.Item>

          {newKey.trim() && !keyError ? (
            <div
              style={{
                background: "#f3f4f6",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 12,
                color: "#374151",
              }}
            >
              <strong>Preview:</strong>{" "}
              <code style={{ fontSize: 12 }}>{`{{${newKey.trim()}}}`}</code>
              <Tag style={{ marginLeft: 8, fontSize: 10 }}>{newType}</Tag>
              {newRequired ? <Tag color="red" style={{ fontSize: 10 }}>required</Tag> : null}
            </div>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
};

export default MergeVariablesSidebar;
